import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Header.css";
import { authService } from "../../services/authService";
import Feedback from '../../pages/components/Feedback'
import CreditsBar from '../../components/Common/CreditsBar'
import commonService from "../../services/otherServices";
import Popup from '../../components/Common/Error'
import axios from "axios";


const Header: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [profilePic, setProfilePic] = useState<string>('/images/small-profile.png');
  const [userName, setUserName] = useState<string>('');
  const [userLastName, setUserLastName] = useState<string>('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [showSubmitPopup, setShowSubmitPopup] = useState(false);
  const [showCreditPopup, setShowCreditPopup] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [requestCreditsLoading, setRequestCreditsLoading] = useState(false);
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [credit, setCredit] = useState("");
  const hasFetchedCredit = useRef(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  type CreditDetail = {
    available_credits: number;
    total_credits: number;
    used_credits: number;
  };

  const [creditDetail, setCreditDetail] = useState<CreditDetail>({
    available_credits: 0,
    total_credits: 0,
    used_credits: 0
  });


  // Function to update state from localStorage
  const updateFromLocalStorage = () => {
    const savedProfilePic = localStorage.getItem('profile_pic');
    if (savedProfilePic && savedProfilePic.trim() !== '') {
      setProfilePic(savedProfilePic);
    }

    const name = localStorage.getItem('name') || '';
    const last_name = localStorage.getItem('last_name') || '';
    setUserName(name);
    setUserLastName(last_name);
    setCreditDetail(localStorage.getItem('credit') ? JSON.parse(localStorage.getItem('credit') || '{}') : null);
  };

  // Initialize from localStorage on mount
  useEffect(() => {
    updateFromLocalStorage();
  }, []);

  // Listen for localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      updateFromLocalStorage();
    };

    // Listen for custom events (from same tab)
    const handleCustomEvent = () => {
      updateFromLocalStorage();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleCustomEvent as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomEvent as EventListener);
    };
  }, []);

  // 🔹 Ref for dropdown container
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const fetchCreditDetails = async () => {

    try {
      const response = await commonService.getCreditDetails();
      // console.log("Credit Details:", response.data);
      setLastUpdatedAt(new Date());
      setCreditDetail({
        available_credits: Number(response?.data?.available_credits) || 0,
        total_credits: Number(response?.data?.total_credits) || 0,
        used_credits: Number(response?.data?.used_credits) || 0
      });
    } catch (error) {
      // setErrorMsg("Failed to fetch credit details.");
    }
  };

  useEffect(() => {
    if (hasFetchedCredit.current) return;
    hasFetchedCredit.current = true;
    fetchCreditDetails();
  }, []);

  const submitRequest = async (credits: any) => {
    try {
      setRequestCreditsLoading(true);
      const payload = { no_of_credits: credits };
      const response = await commonService.submitCreditRequest(payload);
      setShowCreditForm(false);
      setShowCreditPopup(true);
      setCredit("");
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const errorData = err.response?.data;

        if (errorData?.details?.length) {
          setErrorMsg(errorData.details[0]?.input?.message);
        } else if (errorData?.message) {
          setErrorMsg(errorData.message);
        } else {
          setErrorMsg("Something went wrong");
        }
      } else {
        setErrorMsg("Failed to fetch credit details.");
      }
    } finally {
      setRequestCreditsLoading(false);
    }
  };
  // 🔹 Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setCreditOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout API failed:", error);
    } finally {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("role_id");
      localStorage.removeItem("email");
      localStorage.removeItem("permissions");
      navigate("/signin");
    }
  };

  const formatName = (name?: string | null, last_name?: string | null): string => {
    if (!name) return "";
    const firstName = name[0].toUpperCase() + name.slice(1).toLowerCase();
    if (!last_name) return firstName;
    const lastName = last_name[0].toUpperCase() + last_name.slice(1).toLowerCase();
    return firstName + " " + lastName;
  };

  const handleMenuClick = (path: string) => {
    setOpen(false);      // ✅ Close popup
    navigate(path);     // ✅ Navigate
  };

  const formatDateTime = (date?: Date | null): string => {
    if (!date) return "—";

    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };


  return (
    <>
      <header className="app-header">
        {/* Left */}
        <div className="header-left">
          <img src="images/main-logo.png" alt="logo" onClick={() => navigate('/dashboard')} style={{ cursor: "pointer" }} />
        </div>


        {/* Right */}
        <div className="header-right" ref={dropdownRef}>
          <img
            src="/images/wallet.png"
            alt="wallet"
            onClick={(e) => { e.stopPropagation(); setCreditOpen(!creditOpen); setOpen(false); }}
            style={{ cursor: "pointer", marginRight: "18px" }}
          />
          <div className="header-user" onClick={(e) => { e.stopPropagation(); setCreditOpen(false); setOpen(!open) }}>
            <img
              src={profilePic}
              alt="avatar"
              className="user-avatar"
              onError={(e) => {

                e.currentTarget.src = '/images/small-profile.png';
              }}
            />
          </div>

          {creditOpen && (
            <div className="header-credit-dropdown" onClick={(e) => e.stopPropagation()}>
              <div className="dropdown-actions">
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ padding: "10px 14px", fontSize: "12px" }}>Account Credits</span>
                  <span style={{ padding: "10px 14px", fontSize: "12px", fontWeight: 100, color: "blue", cursor: "pointer" }} onClick={() => { setShowCreditForm(true); setCreditOpen(false) }}>Get More Credits?</span>
                </div>
                <span style={{ padding: "10px 14px", fontSize: "12px" }}>
                  {/* <img src="/images/accounts-credits-download.png" style={{ width: "12px" }} alt="" />
                  <span style={{ margin: "4px 10px 0", fontSize: "12px", color: "#d7e4ff" }}>|</span> */}
                  <span style={{ fontSize: "12px" }}>{creditDetail?.used_credits || 0} of {creditDetail?.total_credits || 0} credit used</span>
                  <CreditsBar used={creditDetail?.used_credits || 0} total={creditDetail?.total_credits || 0} />
                </span>
                <span style={{ padding: "10px 14px", fontSize: "11px", fontWeight: 100 }}>
                  Last updated: {formatDateTime(lastUpdatedAt)}<span style={{ color: "blue", marginLeft: "3px", cursor: "pointer" }} onClick={() => fetchCreditDetails()}>Update</span>
                </span>
              </div>
            </div>
          )}

          {open && (
            <div className="header-dropdown" onClick={(e) => e.stopPropagation()}>
              <div className="d-flex flex-row">
                <img
                  style={{ margin: "13px 5px" }}
                  src={profilePic}
                  alt="avatar"
                  className="user-avatar"
                  onError={(e) => {
                    e.currentTarget.src = '/images/small-profile.png';
                  }}
                />
                <div>
                  <p className="drop-name">
                    {formatName(userName, userLastName)}
                  </p>
                  <p className="drop-email">
                    {localStorage.getItem("email")}
                  </p>
                </div>
              </div>

              <div className="dropdown-actions">
                <button
                  onClick={() => handleMenuClick("/my-account")}
                  className="drop-buttons"
                >
                  <img src="/images/new-profile.png" alt="" /> My Account
                </button>

                <button className="drop-buttons">
                  <a style={{ textDecoration: "none", color: "#978f8fff" }} href="https://support.spokesly.com/" target="_blank" rel="noopener noreferrer">
                    <img src="/images/new-feedback.png" alt="" /> Support Center
                  </a>
                </button>

                <button
                  className="drop-buttons"
                  onClick={() => handleMenuClick("/refer-earn")}
                >
                  <img src="/images/r&f.png" alt="" /> Refer & Earn
                </button>
              </div>

              <button className="droplogout" onClick={handleLogout}>
                <img src="/images/sign-out.png" alt="" /> Sign Out
              </button>
            </div>
          )}
        </div>

      </header>

      <div style={{
        position: "fixed",
        right: "26px",
        bottom: "26px"
      }}>
        <img src='/images/chat.png' style={{ width: "45px" }} onClick={() => setShowFeedback(true)} />
      </div>

      <Feedback
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        onSuccess={() => setShowSubmitPopup(true)}
      />
      {showSubmitPopup && (
        <div className="submit-popup-overlay">
          <div className="submit-popup">
            {/* Close icon */}
            <div className="d-flex justify-content-end">
              <img src="/images/close-active.png" alt="" className="submit-popup-close"
                onClick={() => setShowSubmitPopup(false)}
              />
            </div>

            <div className="d-flex justify-content-center">
              <img src="/images/hand-shak.png" alt="" />
            </div>

            {/* Content */}
            <p className="submit-popup-title">Thank you!</p>
            <p className="submit-popup-text mt-3" style={{ textAlign: "center" }}>
              Your feedback has been received. We value your input.
            </p>
          </div>
        </div>

      )}

      {showCreditPopup && (
        <div className="avail-popup-overlay">
          <div className="avail-popup">
            <div className="d-flex flex-row justify-content-end">
              <div
                className="page-icon"
                onClick={() => setShowCreditPopup(false)}
              >
                <img
                  src="/images/close-inactive.png"
                  alt="normal"
                  className="icon-normal"
                />
                <img
                  src="/images/close-active.png"
                  alt="hover"
                  className="icon-hover"
                />
              </div>
            </div>

            <div className='text-center'>
              <img src="/images/hand-shak.png" alt="" />
              <p className="popup-title">Thank you</p>
              <p className="popup-text">
                Your request has been submitted successfully.
                Our <br />BDA will get in touch with you soon.
              </p>
            </div>
          </div>
        </div>
      )}

      {showCreditForm && (
        <div className="download-popup-overlay">
          <div className="download-popup" >
            <div className="d-flex justify-content-end">
              <img
                src="/images/close-active.png"
                alt="close"
                style={{ cursor: "pointer" }}
                onClick={() => { setShowCreditForm(false); setCredit(""); }}
              />
            </div>

            <div className="text-center">
              <div className="mt-2">Kindly specify the number of credits<br /> required.</div>

              <div className="download-wrapper mt-4" style={{ display: "flex", justifyContent: "space-around" }}>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="dashboard-input"
                  style={{ border: "1px solid #115ea3", width: "auto" }}
                  value={credit}
                  placeholder="Enter number of credits"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      setCredit(value);
                    }
                  }}
                />
                <button
                  className="download"
                  disabled={requestCreditsLoading || !credit || Number(credit) <= 0}
                  style={{ borderRadius: "4px", cursor: requestCreditsLoading || !credit || Number(credit) <= 0 ? "not-allowed" : "pointer", backgroundColor: requestCreditsLoading || !credit || Number(credit) <= 0 ? "#888888" : "#115ea3" }}
                  onClick={() => {
                    submitRequest(credit); setCredit("");
                  }}
                >
                  {requestCreditsLoading ? <><span style={{ fontSize: "10px" }} className="spinner-border spinner-border-sm me-2" />Processing...</> : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* error popup */}
      <Popup
        isOpen={!!errorMsg}
        onClose={() => setErrorMsg("")}
        title={errorMsg}
        iconSrc="/images/grey-cancel-cta.png"
      />
    </>
  );
};

export default Header;